/**
 * @fileoverview Dammen Result Overlay Component
 * @description Displays game result with animation
 */

import React, { memo, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Dimensions,
} from "react-native";
import { Image } from "expo-image";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import LottieView from "lottie-react-native";
import { DammenResult, PlayerId, CHARACTER_COLORS } from "../types";
import { getLottieSource } from "@/lib/lottie-registry";

interface DammenResultOverlayProps {
  result: DammenResult;
  myPiece: PlayerId | null;
  myName: string;
  opponentName: string;
  pointsEarned?: number;
  isWeekendMode?: boolean;
  lottieKey?: string;
  onPlayAgain?: () => void;
  onRematch?: () => void;
  onBack: () => void;
}

/**
 * Character SVG images
 */
const CHARACTER_IMAGES: Record<PlayerId, any> = {
  opa: require("@/assets/games/opa_endless_runner.svg"),
  oma: require("@/assets/games/omam_endless_runner.svg"),
};

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");

/**
 * Dammen Result Overlay Component
 */
export const DammenResultOverlay: React.FC<DammenResultOverlayProps> = memo(
  ({
    result,
    myPiece,
    myName,
    opponentName,
    pointsEarned = 0,
    isWeekendMode = false,
    lottieKey,
    onPlayAgain,
    onRematch,
    onBack,
  }) => {
    const colors = useThemeColors();
    const scaleAnim = useRef(new Animated.Value(0)).current;
    const opacityAnim = useRef(new Animated.Value(0)).current;

    const isWin = result.winnerPiece === myPiece && !result.isDraw && !result.isForfeit;
    const isLoss = result.winnerPiece !== myPiece && result.winnerPiece !== null && !result.isDraw;
    const isDraw = result.isDraw;
    const isForfeit = result.isForfeit;

    useEffect(() => {
      Animated.parallel([
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 8,
          tension: 40,
          useNativeDriver: true,
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    }, [scaleAnim, opacityAnim]);

    const getResultTitle = (): string => {
      if (isForfeit) {
        return isLoss ? "Je hebt opgegeven" : "Tegenstander heeft opgegeven";
      }
      if (isDraw) return "Gelijkspel!";
      if (isWin) return "Gewonnen! 🎉";
      return "Verloren 😢";
    };

    const getResultColor = (): string => {
      if (isDraw) return colors.warning;
      if (isWin) return colors.success;
      return colors.error;
    };

    const formatDuration = (seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    const lottieSource = lottieKey ? getLottieSource(lottieKey) : null;

    return (
      <View style={styles.overlay}>
        {/* Lottie animation */}
        {lottieSource && (
          <LottieView
            source={lottieSource}
            autoPlay
            loop={false}
            style={styles.lottie}
          />
        )}

        <Animated.View
          style={[
            styles.container,
            {
              backgroundColor: colors.surface,
              transform: [{ scale: scaleAnim }],
              opacity: opacityAnim,
            },
          ]}
        >
          {/* Result icon/character */}
          <View
            style={[
              styles.iconContainer,
              { backgroundColor: getResultColor() + "20" },
            ]}
          >
            {result.winnerPiece && !isDraw ? (
              <Image
                source={CHARACTER_IMAGES[result.winnerPiece]}
                style={styles.winnerCharacter}
                contentFit="contain"
              />
            ) : (
              <Ionicons
                name={isDraw ? "swap-horizontal" : "close-circle"}
                size={48}
                color={getResultColor()}
              />
            )}
          </View>

          {/* Result title */}
          <Text style={[styles.title, { color: getResultColor() }]}>
            {getResultTitle()}
          </Text>

          {/* Points earned (weekend mode) */}
          {isWeekendMode && pointsEarned > 0 && (
            <View style={[styles.pointsBadge, { backgroundColor: colors.success }]}>
              <Ionicons name="star" size={16} color="#FFFFFF" />
              <Text style={styles.pointsText}>+{pointsEarned} punten</Text>
            </View>
          )}

          {/* Stats */}
          <View style={[styles.statsContainer, { backgroundColor: colors.background }]}>
            <View style={styles.statRow}>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Zetten
              </Text>
              <Text style={[styles.statValue, { color: colors.text }]}>
                {result.moveCount}
              </Text>
            </View>
            <View style={styles.statRow}>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Tijd
              </Text>
              <Text style={[styles.statValue, { color: colors.text }]}>
                {formatDuration(result.duration)}
              </Text>
            </View>
            <View style={styles.statRow}>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Geslagen (Opa)
              </Text>
              <Text style={[styles.statValue, { color: CHARACTER_COLORS.opa }]}>
                {result.capturedByOpa}
              </Text>
            </View>
            <View style={styles.statRow}>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Geslagen (Oma)
              </Text>
              <Text style={[styles.statValue, { color: CHARACTER_COLORS.oma }]}>
                {result.capturedByOma}
              </Text>
            </View>
          </View>

          {/* Buttons */}
          <View style={styles.buttonContainer}>
            {onRematch && (
              <TouchableOpacity
                style={[styles.button, { backgroundColor: colors.primary }]}
                onPress={onRematch}
                accessibilityRole="button"
                accessibilityLabel="Rematch"
              >
                <Ionicons name="refresh" size={20} color="#FFFFFF" />
                <Text style={styles.buttonText}>Rematch</Text>
              </TouchableOpacity>
            )}
            {onPlayAgain && (
              <TouchableOpacity
                style={[styles.button, { backgroundColor: colors.primary }]}
                onPress={onPlayAgain}
                accessibilityRole="button"
                accessibilityLabel="Opnieuw spelen"
              >
                <Ionicons name="play" size={20} color="#FFFFFF" />
                <Text style={styles.buttonText}>Opnieuw</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[
                styles.button,
                styles.secondaryButton,
                { borderColor: colors.border },
              ]}
              onPress={onBack}
              accessibilityRole="button"
              accessibilityLabel="Terug"
            >
              <Text style={[styles.buttonText, { color: colors.text }]}>
                Terug
              </Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    );
  }
);

DammenResultOverlay.displayName = "DammenResultOverlay";

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
  },
  lottie: {
    position: "absolute",
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT * 0.5,
    top: 0,
  },
  container: {
    width: SCREEN_WIDTH * 0.85,
    maxWidth: 360,
    borderRadius: 20,
    padding: 24,
    alignItems: "center",
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  winnerCharacter: {
    width: 60,
    height: 60,
    borderRadius: 30,
  },
  title: {
    fontSize: 24,
    fontWeight: "700",
    marginBottom: 12,
  },
  pointsBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 6,
    marginBottom: 16,
  },
  pointsText: {
    color: "#FFFFFF",
    fontWeight: "700",
    fontSize: 16,
  },
  statsContainer: {
    width: "100%",
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  statRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 6,
  },
  statLabel: {
    fontSize: 14,
  },
  statValue: {
    fontSize: 14,
    fontWeight: "600",
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  button: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
    gap: 8,
  },
  secondaryButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  buttonText: {
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: 14,
  },
});

